import React from 'react';
import { motion } from 'framer-motion';
import { Business, BusinessForm } from '../../types/business';
import { FormInput, FormTextarea } from '../forms';

interface BusinessProfileProps {
  business: Business | null;
  editMode: boolean;
  form: BusinessForm;
  loading: boolean;
  onEditModeToggle: () => void;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onProfileUpdate: () => void;
  onCancel: () => void;
}

export const BusinessProfile: React.FC<BusinessProfileProps> = ({
  business,
  editMode,
  form,
  loading,
  onEditModeToggle,
  onInputChange,
  onProfileUpdate,
  onCancel
}) => {
  if (!business) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-white rounded-xl shadow-lg p-6"
      >
        <div className="text-center py-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            Welcome to Your Business Dashboard
          </h3>
          <p className="text-gray-600 mb-6">
            Complete your business profile to get started with TyreLink.
          </p>
          <button
            onClick={onEditModeToggle}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Create Business Profile
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-lg p-6"
    >
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Business Profile</h2>
        {!editMode && (
          <button
            onClick={onEditModeToggle}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Edit Profile
          </button>
        )}
      </div>

      {editMode ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormInput
              label="Business Name"
              name="name"
              value={form.name}
              onChange={onInputChange}
              required
              placeholder="Enter your business name"
            />
            <FormInput
              label="Contact Email"
              name="contact_email"
              type="email"
              value={form.contact_email}
              onChange={onInputChange}
              required
              placeholder="business@example.com"
            />
          </div>

          <FormTextarea
            label="Business Description"
            name="description"
            value={form.description}
            onChange={onInputChange}
            placeholder="Describe your business and services"
            rows={4}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormInput
              label="Contact Phone"
              name="contact_phone"
              value={form.contact_phone}
              onChange={onInputChange}
              placeholder="+27 11 123 4567"
            />
            <FormInput
              label="Street Address"
              name="address"
              value={form.address}
              onChange={onInputChange}
              placeholder="123 Main Street"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormInput
              label="City"
              name="city"
              value={form.city}
              onChange={onInputChange}
              placeholder="Johannesburg"
            />
            <FormInput
              label="Province"
              name="province"
              value={form.province}
              onChange={onInputChange}
              placeholder="Gauteng"
            />
          </div>

          <div className="flex space-x-4">
            <button
              onClick={onProfileUpdate}
              disabled={loading}
              className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
            <button
              onClick={onCancel}
              disabled={loading}
              className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Name
              </label>
              <p className="text-gray-900 font-semibold">{business.name}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Email
              </label>
              <p className="text-gray-900">{business.contact_email}</p>
            </div>
          </div>

          {business.description && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <p className="text-gray-900">{business.description}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {business.contact_phone && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Phone
                </label>
                <p className="text-gray-900">{business.contact_phone}</p>
              </div>
            )}
            {business.address && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <p className="text-gray-900">
                  {business.address}
                  {business.city && `, ${business.city}`}
                  {business.province && `, ${business.province}`}
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <span className="text-sm font-medium text-gray-700 mr-2">
                Verification Status:
              </span>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  business.verified
                    ? "bg-green-100 text-green-800"
                    : "bg-yellow-100 text-yellow-800"
                }`}
              >
                {business.verified ? "Verified" : "Pending Verification"}
              </span>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};